import type Image from "next/image"
import type { Media } from "#models/media"
import type { Props, PropsWithout } from "#types/utils"

type OImagePropsBaseProps = {
  // Can be set to be lazy loaded with intersection observer
  lazy?: boolean
}
type NonMediaOImageProps_Explicit = {
  media?: undefined
  /*
   * If you want to derive the dimensions from the image itself and set the sizing via CSS - ONLY USE IF IMAGE IS ABSOLUTE OTHERWISE LEADS TO LAYOUT SHIFT
   * use with explicit width (example !w-10) and height !h-auto for example
   */
  noExplicitDimensions?: undefined
} & OImagePropsBaseProps &
  Props<typeof Image> & { width: number; height: number }
type NonMediaOImageProps_NoExplicit = { media?: undefined; noExplicitDimensions: true } & OImagePropsBaseProps & Props<typeof Image>
type NonMediaOImageProps = NonMediaOImageProps_Explicit | NonMediaOImageProps_NoExplicit

type MediaOImageProps = { media: Media } & OImagePropsBaseProps & PropsWithout<typeof Image, "src" | "alt" | "width" | "height" | "blurDataURL">

export type OImageProps = NonMediaOImageProps | MediaOImageProps

export const EMPTY_IMAGE_SRC = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="

/**
 * Converts a media object with additional OImage props to the props required for the OImage component
 * MediaOImageProps -> NonMediaOImageProps
 */
export function mediaToNextOImageProps({ media, ...props }: MediaOImageProps): NonMediaOImageProps {
  return {
    ...props,
    src: media.url,
    alt: media.alternativeText ?? "",
    width: media.width,
    height: media.height,
    blurDataURL: props.placeholder === "blur" ? media.placeholder : undefined,
  }
}

export function getPropsForNextImage(props: OImageProps): { nextImageProps: Props<typeof Image>; nonMediaImageProps: NonMediaOImageProps } {
  const actualProps = props.media ? mediaToNextOImageProps(props) : props

  const { width, height, noExplicitDimensions, ...propsWithoutDimensions } = actualProps

  // When fill is true, next/image forbids width and height props
  return { nextImageProps: props.fill ? propsWithoutDimensions : actualProps, nonMediaImageProps: actualProps }
}
